import React, { useCallback, useEffect, useState } from 'react';
import { checkUpcomingCallbacks, loadOktellCall, loadOperatorContour } from 'api';
import InfoByPhoneDialog from 'dialogs/InfoByPhoneDialog';
import { Button, ButtonProps, IconButton } from '@material-ui/core';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import { useHistory } from 'react-router-dom';
import { getPath as getPageCloseApplicationsPath } from 'pages/PageApplicationToCloseList';
import { Close } from '@material-ui/icons';
import { USER_OPERATIONS } from 'helper';
import { RootState } from 'interfaces';
import { useSelector } from 'react-redux';
import {
  detectPrefixByContour,
  detectPrefixByPhoneNumber,
  excludePrefixFromPhoneNumber,
  hasKnownPrefix,
  normalizeIncomingPhoneNumber,
} from 'components/TelInput/helpers';

interface ICallback {
  callbackTimestamp: string;
  phone: string;
}

// Пропсы принимаем и прокидываем в Button: ButtonGroup клонирует прямых детей
// и передаёт им className с классами группы — на нём и держатся радиусы крайних кнопок.
const AcceptCallButton = (props: ButtonProps) => {
  const history = useHistory();
  const operations = useSelector((state: RootState) => state.operations);

  const [incomingPhoneNumber, setIncomingPhoneNumber] = useState<string>('');
  const [phonePrefix, setPhonePrefix] = useState<string>('+7');
  const [chainId, setChainId] = useState<string>('');
  const [opened, setOpened] = useState<boolean>(false);
  const [callbacks, setCallbacks] = useState<ICallback[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  // Звонка нет — код страны берём по контуру оператора, иначе узбекскому оператору показывается +7
  const operatorPrefix = () =>
    loadOperatorContour()
      .then(({ contour }) => detectPrefixByContour(contour))
      .catch(() => detectPrefixByContour(null));

  const handleClick = () => {
    setIsLoading(true);

    loadOktellCall()
      .then((response) => {
        // Длина префикса у КЗ и УЗ разная, поэтому отрезаем не одну цифру, а найденный префикс.
        const incoming = normalizeIncomingPhoneNumber(response.incomingNumber);
        // Код страны в самом номере важнее контура: оператору могут позвонить и из соседней страны.
        const prefix = hasKnownPrefix(incoming) ? detectPrefixByPhoneNumber(incoming) : detectPrefixByContour(response.contour);

        setPhonePrefix(prefix);
        setIncomingPhoneNumber(excludePrefixFromPhoneNumber(incoming).replaceAll(/\D/g, ''));
        setChainId(response.chainId);
        setOpened(true);
      })
      .catch(async () => {
        setPhonePrefix(await operatorPrefix());
        setIncomingPhoneNumber('');
        setChainId('');
        setOpened(true);
      })
      .finally(() => setIsLoading(false));
  };

  const redirect = useCallback(
    (phoneNumber: string) => {
      const handleCloseSnackbar = (phoneNumber: string) => {
        const lsItem = localStorage.getItem('dismissed-phones');
        if (lsItem) {
          const dismissedPhones = JSON.parse(lsItem);
          const newDP = [...dismissedPhones, phoneNumber];
          localStorage.setItem('dismissed-phones', JSON.stringify(newDP));
        } else {
          localStorage.setItem('dismissed-phones', JSON.stringify([phoneNumber]));
        }
        closeSnackbar(phoneNumber);
      };

      return (
        <>
          <Button
            onClick={() => history.push(getPageCloseApplicationsPath(phoneNumber))}
            variant="text"
            color="inherit"
          >
            Открыть
          </Button>
          <IconButton onClick={() => handleCloseSnackbar(phoneNumber)} size="small" style={{ color: 'white' }}>
            <Close />
          </IconButton>
        </>
      );
    },
    [history, closeSnackbar],
  );

  useEffect(() => {
    callbacks
      .filter(({ callbackTimestamp }) => {
        const callbackTime = moment(callbackTimestamp);
        const now = moment(new Date());

        return now.isBefore(callbackTime);
      })
      .filter(({ phone }) => {
        const lsItem = localStorage.getItem('dismissed-phones');

        if (lsItem) {
          const dismissedPhones = JSON.parse(lsItem);
          return !dismissedPhones.includes(phone);
        } else return true;
      })
      .forEach(({ callbackTimestamp, phone }) =>
        enqueueSnackbar(`У вас назначен перезвон через ${getMinutesLeft(callbackTimestamp)}`, {
          action: () => redirect(phone),
          autoHideDuration: 59 * 1000,
          key: phone,
          variant: 'info',
        }),
      );
  }, [callbacks, enqueueSnackbar, redirect]);

  useEffect(() => {
    if (operations) {
      if (operations.includes(USER_OPERATIONS.CALLCENTER_CALLBACK_OPERATION)) {
        const interval = setInterval(() => {
          checkUpcomingCallbacks(new Date().toISOString()).then(setCallbacks);
        }, 60 * 1000);
        return () => clearInterval(interval);
      }
    }
  }, [operations]);

  return (
    <>
      <Button
        {...props}
        style={{ backgroundColor: '#27AE60', color: 'inherit', ...props.style }}
        variant="contained"
        onClick={handleClick}
        disabled={isLoading}
      >
        Принять
      </Button>

      <InfoByPhoneDialog
        open={opened}
        callChainId={chainId}
        incomingPhoneNumber={incomingPhoneNumber}
        phonePrefix={phonePrefix}
        onClose={() => setOpened(false)}
      />
    </>
  );
};

function getMinutesLeft(date: string) {
  const end = moment(date);
  const start = moment(new Date());
  const duration = moment.duration(end.diff(start));
  return getEnding(Math.abs(Math.floor(duration.asMinutes())));
}

function getEnding(num: number) {
  if (num === 1) {
    return `${num} минуту`;
  } else if (num > 1 && num < 5) {
    return `${num} минуты`;
  } else {
    return `${num} минут`;
  }
}

export default AcceptCallButton;
